import React, { useState } from 'react';
import { ArrowRight, BarChart3, ClipboardList, Eye, EyeOff, FileText, Lock, Mail, Truck } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import logoLockup from '../assets/logo-lockup.png';
import heroImage from '../assets/login-hero.jpg';
import { t } from '../i18n';

const REMEMBER_KEY = 'kota-remember-email';

const FEATURES = [
  { icon: BarChart3, label: 'Gestión de desarrollo de proyectos y fases ambientales' },
  { icon: Truck, label: 'Compras y Logística' },
  { icon: ClipboardList, label: 'Control y Trazabilidad' },
  { icon: FileText, label: 'Reportes y Análisis' }
];

// Velo claro arriba a la izquierda para que el titular oscuro se lea sobre el cielo.
const SKY_VEIL =
  'radial-gradient(130% 62% at 0% 12%, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.45) 50%, rgba(255, 255, 255, 0) 80%)';
const SKY_TOP = 'linear-gradient(180deg, #2f78c4 0%, #3481cd 100%)';
const HERO_MASK = 'linear-gradient(180deg, transparent 0%, #000 14%)';
const BOTTOM_FADE = 'linear-gradient(0deg, #0d1d39 0%, #0d1d39 15%, rgba(13, 29, 57, 0.55) 21%, rgba(13, 29, 57, 0) 30%)';

const readRememberedEmail = () => {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) || '';
  } catch {
    return '';
  }
};

const Login = () => {
  const { login } = useAppContext();
  const rememberedEmail = readRememberedEmail();
  const [formState, setFormState] = useState({ email: rememberedEmail, password: '' });
  const [remember, setRemember] = useState(Boolean(rememberedEmail));
  const [showPassword, setShowPassword] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(formState.email, formState.password);
    if (result.success) {
      try {
        if (remember) window.localStorage.setItem(REMEMBER_KEY, formState.email.trim());
        else window.localStorage.removeItem(REMEMBER_KEY);
      } catch {
        // sin almacenamiento local no se recuerda el correo
      }
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[53fr_47fr] bg-[#f5f8fc]">
      <section className="relative hidden lg:flex flex-col overflow-hidden" style={{ background: SKY_TOP }}>
        {/* La foto va a todo el ancho, apoyada sobre la franja inferior; arriba continúa el cielo. */}
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          className="absolute inset-x-0 w-full h-auto select-none pointer-events-none"
          style={{ bottom: 'calc(175px - 2.8vw)', maskImage: HERO_MASK, WebkitMaskImage: HERO_MASK }}
        />
        <div className="absolute inset-0" style={{ background: SKY_VEIL }} />
        <div className="absolute inset-0" style={{ background: BOTTOM_FADE }} />

        <div className="relative z-10 px-12 xl:px-24 pt-20 xl:pt-28 max-w-2xl">
          <img src={logoLockup} alt="KOTA+ Energy System" className="h-20 xl:h-24 w-auto" />
          <h1 className="mt-10 text-[32px] leading-[1.2] xl:text-[40px] font-bold text-[#0a1633]">
            {t('Soluciones integradas para proyectos de energía renovable')}
          </h1>
          <p className="mt-4 text-[18px] leading-[1.45] xl:text-[21px] text-slate-600">
            {t('Gestión, abastecimiento y control en una sola plataforma.')}
          </p>
        </div>

        <ul className="relative z-10 mt-auto grid grid-cols-4 px-6 xl:px-10 pb-10 text-white">
          {FEATURES.map(({ icon: Icon, label }, index) => (
            <li
              key={label}
              className={`flex flex-col items-center justify-start gap-3 px-3 text-center ${
                index > 0 ? 'border-l border-white/30' : ''
              }`}
            >
              <Icon className="w-9 h-9" strokeWidth={1.5} />
              <span className="text-[14px] xl:text-[16px] font-medium leading-snug">{t(label)}</span>
            </li>
          ))}
        </ul>
      </section>

      <main className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-[560px] bg-white rounded-2xl shadow-[0_12px_48px_rgba(15,40,80,0.08)] px-6 py-10 sm:px-10 sm:py-14">
          <div className="flex justify-center">
            <img src={logoLockup} alt="KOTA+ Energy System" className="h-16 sm:h-20 w-auto" />
          </div>
          <h2 className="mt-8 text-center text-[26px] sm:text-[32px] leading-tight font-bold text-[#040924]">{t('Ingreso al sistema')}</h2>
          <p className="mt-2 text-center text-[16px] sm:text-[19px] text-slate-500">{t('Accede a tu cuenta para continuar.')}</p>

          <form className="mt-10 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="login-email" className="block text-[15px] font-medium text-slate-800">
                {t('Correo electrónico')}
              </label>
              <div className="relative mt-2">
                <Mail className="w-5 h-5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  value={formState.email}
                  onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                  className="w-full h-14 pl-12 pr-4 text-[16px] rounded-lg border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0b52a2]/20 focus:border-[#0b52a2]"
                  placeholder="tuusuario@kotaplus.cl"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="block text-[15px] font-medium text-slate-800">
                {t('Contraseña')}
              </label>
              <div className="relative mt-2">
                <Lock className="w-5 h-5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formState.password}
                  onChange={(event) => setFormState((prev) => ({ ...prev, password: event.target.value }))}
                  className="w-full h-14 pl-12 pr-12 text-[16px] rounded-lg border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0b52a2]/20 focus:border-[#0b52a2]"
                  placeholder={t('Ingresa tu contraseña')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? t('Ocultar contraseña') : t('Mostrar contraseña')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded text-slate-500 hover:text-slate-800"
                >
                  {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 flex-wrap">
              <label className="flex items-center gap-3 text-[15px] text-slate-800 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                  className="w-5 h-5 rounded border-slate-300 text-[#0b52a2] focus:ring-[#0b52a2]/30"
                />
                {t('Recordarme en este dispositivo')}
              </label>
              <button
                type="button"
                onClick={() => setShowRecovery((prev) => !prev)}
                className="text-[15px] font-medium text-[#1a56db] hover:underline"
              >
                {t('¿Olvidaste tu contraseña?')}
              </button>
            </div>

            {showRecovery && (
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                {t('Contacta al administrador del sistema para restablecer tu contraseña.')}
              </div>
            )}

            {error && (
              <div role="alert" className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                {t(error)}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 flex items-center justify-center gap-3 bg-[#0b52a2] text-white text-[18px] font-semibold rounded-lg hover:bg-[#094689] disabled:opacity-60"
            >
              {loading ? t('Ingresando...') : t('Ingresar')}
              {!loading && <ArrowRight className="w-5 h-5" />}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Login;
